import { useCallback } from 'react';
import { useQueries, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { logger } from '@utils/logger';
import { questionEntityKey, dedupeIds } from '@lib/cache/questions';
import type { QuestionCacheItem } from '@lib/cache/questions';

interface UseQuestionEntitiesOptions {
  gcTime?: number;
}

/**
 * Subscribes to the entity store for a list of question IDs so optimistic
 * writes (useQuestionInteractionsOptimistic, useChat)
 * propagate to every list surface without prop drilling or re-fetching.
 * Every list hook that renders from the entity store shares this one
 * subscription instead of re-declaring the same useQueries block.
 *
 * AIDEV-NOTE: dedupe (first-occurrence-wins) lives HERE, not in each feed hook, because
 * useQueries subscribes once per array ENTRY - a repeated id would mount two query
 * definitions on the same queryKey/React key and render the entity twice. Every list hook
 * that flatMaps idPages.pages into `ids` can hand back a duplicate id when its backing store
 * reorders/rescores mid-scroll (backend #680 tracks the server-side snapshot-pagination
 * fix); one guarantee point here covers all of them instead of each call site
 * re-implementing it.
 */
export const useQuestionEntities = (
  ids: string[],
  { gcTime = 10 * 60 * 1000 }: UseQuestionEntitiesOptions = {},
): QuestionCacheItem[] => {
  const queryClient = useQueryClient();

  const dedupedIds = dedupeIds(ids);

  // Content-derived key, recomputed every render (cheap - just a join) so
  // callers never need to memoize the `ids` array they pass in. `combine`
  // depends on this key rather than on `ids` itself, so its identity only
  // changes when the id SET actually changes - never on every render just
  // because a caller minted a fresh array reference.
  //
  // This isn't just an optimization: TanStack Query's QueriesObserver caches
  // combine's output keyed on (combine reference, its own internal result
  // reference), and that internal result is only refreshed via a
  // post-commit effect inside useQueries. If combine's reference is
  // unchanged on a render where the id set grows (e.g. "load more"
  // pagination), the newly added query's already-fresh cache data is
  // skipped for that render even though it's available - the combined
  // array is missing the new id until one extra render later, when the
  // effect's setQueries call forces a refresh. Keying combine off idsKey
  // closes that gap: an id-set change gives combine a new reference,
  // which forces recombination from the fresh per-render result
  // immediately instead of waiting on the effect-driven notify.
  const idsKey = dedupedIds.join(',');

  const combine = useCallback(
    (results: UseQueryResult<QuestionCacheItem>[]) =>
      results.flatMap(r => (r.data ? [r.data] : [])),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- idsKey (not ids) is the intentional dependency; see comment above.
    [idsKey],
  );

  return useQueries({
    queries: dedupedIds.map(id => ({
      queryKey: questionEntityKey(id),
      queryFn: (): Promise<QuestionCacheItem> => {
        const existing = queryClient.getQueryData<QuestionCacheItem>(questionEntityKey(id));
        if (existing) return Promise.resolve(existing);
        logger.warn('[entity-store] ids in list but entity missing', [id]);
        return Promise.reject(new Error(`Entity for question ${id} not found in cache`));
      },
      // A cache miss means a producer failed to seed — a programming error,
      // not a transient failure. Retrying only delays the error state ~7s.
      // A later upsert (setQueryData) still self-heals the errored query.
      retry: false,
      staleTime: Infinity,
      gcTime,
    })),
    combine,
  });
};
